/**
 * /app/$tenantSlug/me/details/$itemId — one Ask for Details item.
 *
 * Reuses the joiner step screens (components/joiner/joiner-steps.tsx) but
 * saves through the normal authenticated API (cookies + CSRF) via
 * StepApiContext. The sticky button says "Send to HR".
 */

import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { saveMyDetailItem, uploadMyDetailDocument } from "@/lib/api/detail-requests";
import { meFromRequest } from "@/lib/detail-step-adapter";
import { useInvalidateMyDetails, useMyDetailRequests } from "@/hooks/use-detail-requests";
import { useAuth } from "@/lib/auth";
import { useTenantSlug } from "@/lib/use-tenant-slug";
import type { JoinerDoc } from "@/types/joiner";
import { InboxPage } from "@/components/inbox/inbox-page";
import { StepApiContext, type StepApiValue } from "@/components/joiner/step-api";
import { StepScreen } from "@/components/joiner/joiner-steps";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/details/$itemId")({
  component: MyDetailItemRoute,
});

function MyDetailItemRoute() {
  const tenantSlug = useTenantSlug();
  const { itemId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const refresh = useInvalidateMyDetails();
  const q = useMyDetailRequests();
  const id = Number(itemId);

  const found = useMemo(() => {
    for (const r of q.data?.requests ?? []) {
      if (r.status !== "open") continue;
      const item = r.items.find((i) => i.id === id);
      if (item) return { request: r, item };
    }
    return null;
  }, [q.data, id]);

  const toList = () => void navigate({ to: "/app/$tenantSlug/me/details", params: { tenantSlug } });

  const value = useMemo<StepApiValue | null>(() => {
    if (!found) return null;
    const requestId = found.request.id;
    return {
      submitLabel: "Send to HR",
      backLabel: "Back to Your Details",
      order: found.request.items.map((i) => i.id),
      api: {
        saveStep: (stepId, body) => saveMyDetailItem(requestId, stepId, body),
        uploadDocument: async (stepId, docType, file, fileName, onProgress) =>
          (await uploadMyDetailDocument(requestId, stepId, docType, file, fileName, onProgress)) as JoinerDoc,
        policy: () => Promise.reject(new Error("Policies are not part of this request.")),
      },
    };
  }, [found]);

  const me = useMemo(
    () => (found ? meFromRequest(found.request, user?.first_name ?? "") : null),
    [found, user?.first_name],
  );
  const step = me?.steps.find((s) => s.id === id);

  return (
    <InboxPage title="Finish Your Details">
      {q.isLoading && (
        <div className="max-w-md space-y-3" aria-busy="true">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {q.isError && (
        <Alert variant="destructive" className="max-w-md">
          <AlertTitle>Could not load this request</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.data && !found && (
        <EmptyState
          title="This request is no longer open"
          description="HR may have closed it, or you already sent everything."
          action={
            <Button type="button" variant="outline" className="min-h-11" onClick={toList}>
              Back to Your Details
            </Button>
          }
          className="max-w-md"
        />
      )}

      {found && me && step && value && (
        <div className="max-w-md">
          <StepApiContext.Provider value={value}>
            <StepScreen
              key={`${step.id}-${step.status}`}
              step={step}
              me={me}
              onSaved={async () => {
                await refresh();
                toast.success("Sent to HR.");
                toList();
              }}
              onReload={async () => {
                await refresh();
              }}
              onBack={toList}
              onFatal={(v) => {
                toast.error(v.go ? "This request is no longer open." : v.message);
                if (v.go) toList();
              }}
            />
          </StepApiContext.Provider>
        </div>
      )}
    </InboxPage>
  );
}
